import { isBooleanOrderComponent } from '@moko/utils';
import {
  Checkbox,
  Dropdown,
  Input,
  Option,
  Text,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import ContentSkeleton from '../../ContentSkeleton/ContentSkeleton';
import { useOptionalOtherInfoContext } from '../OtherInfoContext';
import { FieldWrapper } from './FieldWrapper';
import { useFieldReadOnly } from './fieldReadOnly';
import type { FieldComponentProps } from './types';

export const OrderComponentField = ({
  field,
  fieldValue,
  isFieldDisabled,
  validationMessage,
}: FieldComponentProps) => {
  const { t } = useTranslation();
  const context = useOptionalOtherInfoContext();
  const isReadOnly = useFieldReadOnly(field);

  if (field.componentCode === 'TIME_SERVICE') {
    const options = context?.params.orderComponentOptions?.TIME_SERVICE ?? [];
    const selectedCode = typeof fieldValue === 'string' ? fieldValue : '';
    const selected = options.find((option) => option.key === selectedCode);
    if (!context?.params.orderComponentOptions) {
      return (
        <FieldWrapper field={field} validationMessage={validationMessage}>
          <Input
            value={selectedCode}
            placeholder="Speed selection is available in the Order workflow"
            readOnly
            disabled
            aria-label={`${field.label} preview`}
          />
        </FieldWrapper>
      );
    }
    if (isReadOnly) {
      return (
        <FieldWrapper field={field} validationMessage={validationMessage}>
          <Input
            value={selected?.value ?? (selectedCode || t('notSet', 'Not set'))}
            readOnly
            disabled
            aria-label={field.label}
          />
        </FieldWrapper>
      );
    }
    if (context?.params.orderComponentOptionsLoading) {
      return (
        <FieldWrapper field={field} validationMessage={validationMessage}>
          <ContentSkeleton
            variant="lines"
            rows={1}
            label={t('loadingTimeServices', 'Loading available Speed options…')}
          />
        </FieldWrapper>
      );
    }
    if (context?.params.orderComponentOptionsError) {
      return (
        <FieldWrapper field={field} validationMessage={validationMessage}>
          <Text role="alert">{context.params.orderComponentOptionsError}</Text>
        </FieldWrapper>
      );
    }
    return (
      <FieldWrapper field={field} validationMessage={validationMessage}>
        {options.length > 0 ? (
          <Dropdown
            aria-label={field.label}
            placeholder={field.placeholder ?? t('selectSpeed', 'Select Speed')}
            value={selected?.value ?? ''}
            selectedOptions={selectedCode ? [selectedCode] : []}
            disabled={isFieldDisabled}
            onOptionSelect={(_, data) => {
              if (data.optionValue) {
                context?.handleOtherInfoChange(field.name, data.optionValue);
              }
            }}
          >
            {options.map((option) => (
              <Option
                key={option.key}
                value={option.key}
                text={option.value}
                disabled={option.disabled}
              >
                {option.value}
              </Option>
            ))}
          </Dropdown>
        ) : (
          <Text role="status">
            {t(
              'noTimeServicesAvailable',
              'No active Speed options are available. Contact your administrator.'
            )}
          </Text>
        )}
      </FieldWrapper>
    );
  }

  if (isBooleanOrderComponent(field.componentCode)) {
    const checked = fieldValue === true || fieldValue === 'true';
    const consentNote =
      field.componentCode === 'WEEKEND_CONSENT'
        ? t(
            'weekendConsentNote',
            'You consent to a possible weekend fee. This is permission only: it does not schedule a visit and you are not charged now.'
          )
        : undefined;
    return (
      <FieldWrapper field={field} validationMessage={validationMessage}>
        {!context ? (
          <Checkbox
            disabled
            aria-label={`${field.label} preview`}
            label={field.label || 'Additional service'}
          />
        ) : isReadOnly ? (
          <Input
            value={checked ? t('yes', 'Yes') : t('no', 'No')}
            readOnly
            disabled
            aria-label={field.label}
          />
        ) : (
          <Checkbox
            checked={checked}
            disabled={isFieldDisabled}
            aria-label={field.label}
            onChange={(_, data) =>
              context.handleOtherInfoChange(field.name, Boolean(data.checked))
            }
          />
        )}
        {consentNote && <Text size={200}>{consentNote}</Text>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      <Text role="status">
        {field.componentCode
          ? `Unsupported Order Component: ${field.componentCode}`
          : 'Choose a component type before publishing.'}
      </Text>
    </FieldWrapper>
  );
};
